import React, { useEffect, useState } from "react";
import AdminLayout from "@/components/AdminLayout";
import api from "@/lib/apiClient";
import { useAuth } from "@/lib/AuthContext";
import { useLang } from "@/lib/i18n";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { ShieldCheck, ShieldOff } from "lucide-react";

export default function Admin2FA() {
  const { lang } = useLang();
  const { user, refresh } = useAuth();
  const [setup, setSetup] = useState(null);
  const [code, setCode] = useState("");
  const [loading, setLoading] = useState(false);
  const [disableCode, setDisableCode] = useState("");
  const enabled = !!user?.totp_enabled;

  const startSetup = async () => {
    setLoading(true);
    try { const { data } = await api.post("/auth/2fa/setup"); setSetup(data); }
    finally { setLoading(false); }
  };
  const enable = async () => {
    try { await api.post("/auth/2fa/enable", { code }); toast.success("2FA enabled"); setSetup(null); setCode(""); await refresh(); }
    catch (e) { toast.error(e?.response?.data?.detail || e.message); }
  };
  const disable = async () => {
    try { await api.post("/auth/2fa/disable", { code: disableCode }); toast.success("2FA disabled"); setDisableCode(""); await refresh(); }
    catch (e) { toast.error(e?.response?.data?.detail || e.message); }
  };

  return (
    <AdminLayout>
      <h1 className="font-display text-3xl tracking-tight mb-8">Two-Factor Authentication</h1>
      <div className="max-w-xl p-6 rounded-2xl border border-border bg-card space-y-5">
        <div className="flex items-center gap-3">
          {enabled ? <ShieldCheck className="w-8 h-8 text-sage-500" /> : <ShieldOff className="w-8 h-8 text-muted-foreground" />}
          <div>
            <div className="font-display text-lg">{enabled ? (lang === "id" ? "2FA Aktif" : "2FA Enabled") : (lang === "id" ? "2FA Tidak Aktif" : "2FA Disabled")}</div>
            <div className="text-sm text-muted-foreground">{lang === "id" ? "Tambahkan lapisan keamanan dengan aplikasi authenticator (Google Authenticator, Authy)." : "Add a security layer via authenticator app."}</div>
          </div>
        </div>

        {!enabled && !setup && (
          <Button data-testid="2fa-start-setup" onClick={startSetup} disabled={loading} className="rounded-full bg-terracotta-500 hover:bg-terracotta-600 text-cream-50">
            {lang === "id" ? "Mulai Setup" : "Start Setup"}
          </Button>
        )}

        {!enabled && setup && (
          <div className="space-y-4 pt-3 border-t border-border">
            <div>
              <Label className="text-xs uppercase tracking-wider text-muted-foreground">1. {lang === "id" ? "Scan QR" : "Scan QR"}</Label>
              <div className="mt-2 p-4 rounded-xl bg-cream-50 border border-border grid place-items-center">
                <img src={setup.qr_data_url} alt="2FA QR" className="w-48 h-48" data-testid="2fa-qr" />
              </div>
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wider text-muted-foreground">{lang === "id" ? "Atau input manual" : "Or manual entry"}</Label>
              <div className="font-mono text-xs bg-secondary p-3 rounded border border-border break-all mt-1" data-testid="2fa-secret">{setup.secret}</div>
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wider text-muted-foreground">2. {lang === "id" ? "Kode 6-digit" : "6-digit code"}</Label>
              <Input data-testid="2fa-enable-code" value={code} onChange={(e) => setCode(e.target.value.replace(/\D/g,"").slice(0,6))} maxLength={6} className="h-11 font-mono tracking-widest text-center text-lg mt-1" />
            </div>
            <Button data-testid="2fa-enable-btn" onClick={enable} disabled={code.length !== 6} className="rounded-full bg-terracotta-500 hover:bg-terracotta-600 text-cream-50">
              {lang === "id" ? "Aktifkan 2FA" : "Enable 2FA"}
            </Button>
          </div>
        )}

        {enabled && (
          <div className="pt-3 border-t border-border space-y-3">
            <Label className="text-xs uppercase tracking-wider text-muted-foreground">{lang === "id" ? "Kode untuk menonaktifkan" : "Code to disable"}</Label>
            <Input data-testid="2fa-disable-code" value={disableCode} onChange={(e) => setDisableCode(e.target.value.replace(/\D/g,"").slice(0,6))} maxLength={6} className="h-11 font-mono tracking-widest text-center text-lg" />
            <Button data-testid="2fa-disable-btn" variant="outline" onClick={disable} disabled={disableCode.length !== 6}>
              {lang === "id" ? "Nonaktifkan" : "Disable"}
            </Button>
          </div>
        )}
      </div>
    </AdminLayout>
  );
}
